'use client'

import { useEffect, useRef, useState } from 'react'

export type PriceFlashDirection = 'up' | 'down'

interface PriceFlashState {
  direction: PriceFlashDirection | null
  // Bump on every change so consumers can `key={flashKey}` an element to remount it,
  // which restarts the CSS flash animation even if direction is unchanged.
  flashKey: number
  prevValue: number | null
}

const INITIAL_STATE: PriceFlashState = { direction: null, flashKey: 0, prevValue: null }

/**
 * Tracks a numeric value across renders and reports the direction of each change,
 * for driving a CSS flash animation (see `price-flash-up`/`-down` in globals.css)
 * instead of a sticky color that never resets.
 *
 * @param resetKey - when this changes (e.g. a symbol switch), the tracked value and
 * direction are cleared without flashing, so a stale color can't carry over to unrelated data.
 */
export function usePriceFlash(value: number | null | undefined, resetKey?: unknown) {
  const [state, setState] = useState<PriceFlashState>(INITIAL_STATE)
  const valueRef = useRef<number | null>(value ?? null)
  const resetKeyRef = useRef(resetKey)

  useEffect(() => {
    if (resetKey !== resetKeyRef.current) {
      resetKeyRef.current = resetKey
      valueRef.current = null
      setState(INITIAL_STATE)
    }
  }, [resetKey])

  useEffect(() => {
    if (value == null) return
    const prev = valueRef.current
    if (prev !== null && value !== prev) {
      setState((s) => ({
        direction: value > prev ? 'up' : 'down',
        flashKey: s.flashKey + 1,
        prevValue: prev,
      }))
    }
    valueRef.current = value
  }, [value])

  return state
}
